import { useState, useEffect, useRef } from 'react';
import { Box, Text, useColorModeValue, Flex } from '@chakra-ui/react';
import {
  Table,
  TableHead,
  TableHeaderSortCell,
  TableSortDirection,
  TableBody,
  TableRow,
  TableCell,
  useNexus,
  IconButton,
  Button,
  FullScreenLoader,
} from '@spotoninc/nexus-react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  DownloadIcon,
} from '@spotoninc/nexus-web-assets';
import { fetchWithAuth } from '../../utils/apiHelpers';
import { MAX_RECORDS } from '../../utils/constants';

const TransactionDetailPage = ({
  accountKey,
  location,
  fromDate,
  toDate,
  periodFrom,
  periodTo,
  setSelectedTransaction,
  rangeFormat,
}) => {
  const isPeriodRange = rangeFormat === 'period';
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const [currentPage, setCurrentPage] = useState(1);
  const [transactions, setTransactions] = useState({ data: [], columns: [] });
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: TableSortDirection.ascending,
  });

  // Ref to track the last fetch parameters to prevent duplicate calls
  const lastFetchParamsRef = useRef(null);
  //TODO: Remove this state once the backend pagination bug is fixed
  const [previousPageLastItem, setPreviousPageLastItem] = useState(null);

  const itemsPerPage = 10;
  const {
    theme: { tokens },
  } = useNexus();
  //TODO: Remove this logic once the BE returns the right items
  //TODO: Remove these functions once the backend pagination bug is fixed
  // Function to create a unique identifier for a transaction
  // Handles both QA (display-named keys) and Prod (raw backend keys) reliably
  // If the expected display keys are missing, fall back to a short, stable hash
  // built from a limited subset of keys to avoid large strings/perf hits
  const createTransactionId = transaction => {
    if (!transaction || typeof transaction !== 'object') return '';

    // First try with common display and backend keys
    const candidateFields = [
      // Dates
      'Date',
      'Transaction Date',
      'tx_date',
      // Amounts
      'Amount',
      'Tax Amount',
      'subt_nat_amount',
      // Identifiers
      'Description',
      'Memo/Description',
      'doc_num',
      'Num',
      'Name',
      'name',
      'dept_name',
      'Location',
    ];

    const picked = candidateFields
      .map(k =>
        transaction[k] !== undefined && transaction[k] !== null
          ? String(transaction[k])
          : ''
      )
      .filter(Boolean);

    if (picked.length >= 2) {
      // Enough signal to create a reasonably unique ID
      return picked.join('|');
    }

    // Fallback: compute a short, stable hash from a subset of keys
    const hashString = str => {
      let hash = 5381;
      for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) + hash) ^ str.charCodeAt(i); // djb2-xor
      }
      return (hash >>> 0).toString(36);
    };

    const keys = Object.keys(transaction).sort();
    const MAX_KEYS = 24; // limit breadth
    const parts = [];
    for (let i = 0; i < Math.min(keys.length, MAX_KEYS); i++) {
      const k = keys[i];
      const v = transaction[k];
      let s = '';
      if (v === null || v === undefined) {
        s = '';
      } else if (
        typeof v === 'string' ||
        typeof v === 'number' ||
        typeof v === 'boolean'
      ) {
        s = String(v);
      } else {
        try {
          s = JSON.stringify(v);
        } catch (_) {
          s = String(v);
        }
      }
      parts.push(`${k}:${s.slice(0, 64)}`); // cap value length
    }
    const signature = parts.join('|');
    return `h:${hashString(signature)}`;
  };

  //TODO: Remove this function once the backend pagination bug is fixed
  // Function to remove duplicate transactions
  const removeDuplicateTransactions = (newData, previousLastItem) => {
    if (!newData || !Array.isArray(newData) || !previousLastItem) {
      return newData;
    }

    const previousLastItemId = createTransactionId(previousLastItem);

    // Filter out any transaction that matches the last item from the previous page
    return newData.filter(transaction => {
      const currentTransactionId = createTransactionId(transaction);
      return currentTransactionId !== previousLastItemId;
    });
  };

  const formatDateForAPI = dateStr => {
    // If the date is already in MM-DD-YYYY format, return it
    if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
      return dateStr;
    }

    // If the date is in ISO format (YYYY-MM-DD), convert it to MM-DD-YYYY
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split('-');
      return `${month}-${day}-${year}`;
    }

    // If we have a date object or ISO string with time, convert it
    try {
      const date = new Date(dateStr);
      if (!isNaN(date.getTime())) {
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}-${day}-${year}`;
      }
    } catch (e) {
      console.error('Error formatting date:', e);
    }

    // Return the original if we couldn't format it
    return dateStr;
  };

  const buildFormData = (page, recordsPerPage) => {
    const formData = new FormData();
    formData.append('accountKey', accountKey);
    formData.append('activePage', page);
    formData.append('recordsPerPage', recordsPerPage);

    // Add location parameter if available
    if (location) {
      formData.append('location', location);
    }

    // Handle both date range formats
    if (isPeriodRange) {
      // Use the specific period props if available
      if (periodFrom) formData.append('periodFrom', periodFrom);
      if (periodTo) formData.append('periodTo', periodTo);
    } else {
      // Use the specific date props if available and ensure they're valid
      if (fromDate) {
        // Format the date to MM-DD-YYYY as expected by the backend
        const formattedFromDate = formatDateForAPI(fromDate);
        formData.append('fromDate', formattedFromDate);
      }

      if (toDate) {
        // Format the date to MM-DD-YYYY as expected by the backend
        const formattedToDate = formatDateForAPI(toDate);
        formData.append('toDate', formattedToDate);
      }
    }

    return formData;
  };

  const fetchTransactions = async (page = 1) => {
    setIsLoading(true);
    const formData = buildFormData(page, itemsPerPage);

    try {
      const response = await fetchWithAuth('/api/get_transactions/', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      // Simplified data processing to match PLSummary approach
      if (data.transactions && data.totalRecords) {
        let processedData = data.transactions.data;

        //TODO: Remove this duplicate detection logic once the backend pagination bug is fixed
        // Remove duplicates if this is not the first page and we have previous page data
        if (
          page > 1 &&
          previousPageLastItem &&
          processedData &&
          processedData.length > 0
        ) {
          processedData = removeDuplicateTransactions(
            processedData,
            previousPageLastItem
          );
        }

        //TODO: Remove this state tracking once the backend pagination bug is fixed
        // Store the last item of the current page for next page duplicate detection
        if (processedData && processedData.length > 0) {
          setPreviousPageLastItem(processedData[processedData.length - 1]);
        }

        setTransactions({
          ...data.transactions,
          data: processedData,
        });
        setTotalRecords(data.totalRecords.total_rows || 0);
      } else {
        console.error('Unexpected API response format:', data);
        setTransactions({ data: [], columns: [] });
        setTotalRecords(0);
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setTransactions({ data: [], columns: [] });
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllTransactions = async () => {
    const formData = buildFormData(1, MAX_RECORDS);

    try {
      const response = await fetchWithAuth('/api/get_transactions/', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (data.transactions && data.transactions.data) {
        return data.transactions;
      } else {
        console.error(
          'Unexpected API response format for all transactions:',
          data
        );
        return null;
      }
    } catch (err) {
      console.error('Error fetching all transactions:', err);
      return null;
    }
  };

  //TODO: Remove this useEffect once the backend pagination bug is fixed
  // Reset previous page tracking when filters change
  useEffect(() => {
    setPreviousPageLastItem(null);
    // Only reset to page 1 if we're not already on page 1
    // This prevents triggering the fetch useEffect twice on initial load
    // Using functional update to avoid stale closure issues
    setCurrentPage(prevPage => (prevPage !== 1 ? 1 : prevPage));
  }, [
    accountKey,
    fromDate,
    toDate,
    periodFrom,
    periodTo,
    location,
    rangeFormat,
  ]);

  useEffect(() => {
    if (!accountKey) return;

    // Create a string representation of current fetch parameters
    const currentParams = JSON.stringify({
      accountKey,
      fromDate,
      toDate,
      periodFrom,
      periodTo,
      location,
      rangeFormat,
      currentPage,
    });

    // Skip if we're about to make the exact same call
    if (lastFetchParamsRef.current === currentParams) {
      return;
    }

    // Update the ref and make the call
    lastFetchParamsRef.current = currentParams;
    fetchTransactions(currentPage);
  }, [
    accountKey,
    fromDate,
    toDate,
    periodFrom,
    periodTo,
    location,
    rangeFormat,
    currentPage,
  ]);

  const handlePageChange = page => {
    //TODO: Remove this reset logic once the backend pagination bug is fixed
    // Reset previous page tracking when going back to page 1
    if (page === 1) {
      setPreviousPageLastItem(null);
    }
    setCurrentPage(page);
  };

  const totalPages = Math.ceil(totalRecords / itemsPerPage);

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    // Adjust startPage if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Always show first page
    if (startPage > 1) {
      pageNumbers.push(
        <Button
          key={1}
          variant="text"
          onClick={() => handlePageChange(1)}
          isDisabled={isLoading}
          size="small"
        >
          1
        </Button>
      );
      if (startPage > 2) {
        pageNumbers.push(
          <Text key="start-ellipsis" mx={2}>
            ...
          </Text>
        );
      }
    }

    // Add visible page numbers
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <Button
          key={i}
          variant={i === currentPage ? 'filled' : 'text'}
          onClick={() => handlePageChange(i)}
          isDisabled={isLoading}
          size="small"
        >
          {i}
        </Button>
      );
    }

    // Always show last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push(
          <Text key="end-ellipsis" mx={2}>
            ...
          </Text>
        );
      }
      pageNumbers.push(
        <Button
          key={totalPages}
          variant="text"
          onClick={() => handlePageChange(totalPages)}
          isDisabled={isLoading}
          size="small"
        >
          {totalPages}
        </Button>
      );
    }

    return pageNumbers;
  };

  const handleSort = (key, newDirection) => {
    setSortConfig({ key, direction: newDirection });
  };

  const sortData = data => {
    if (!sortConfig.key || !data) return data;

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle numeric columns (amounts)
      if (
        sortConfig.key === 'Amount' ||
        sortConfig.key === 'Tax Amount' ||
        sortConfig.key === 'subt_nat_amount'
      ) {
        aValue = parseFloat(aValue?.toString().replace(/[^0-9.-]+/g, '')) || 0;
        bValue = parseFloat(bValue?.toString().replace(/[^0-9.-]+/g, '')) || 0;
      }
      // Handle date columns
      else if (
        sortConfig.key === 'Date' ||
        sortConfig.key === 'Transaction Date' ||
        sortConfig.key.toLowerCase().includes('date')
      ) {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
      }
      // Handle string columns (case-insensitive)
      else if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      // Handle null/undefined values
      else {
        aValue = aValue || '';
        bValue = bValue || '';
      }

      if (aValue < bValue) {
        return sortConfig.direction === TableSortDirection.ascending ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === TableSortDirection.ascending ? 1 : -1;
      }
      return 0;
    });
  };

  const sortedTransactions = sortData(transactions.data);

  // Function to process location values in QA environment
  const processLocationValue = (columnKey, value) => {
    // Check if we're in QA environment and if this is a location column
    if (
      process.env.REACT_APP_ENV === 'qa' &&
      value &&
      typeof value === 'string' &&
      columnKey === 'Location' // Assuming 'Location' is the key for location column
    ) {
      // TEMP - For NRA Demos only, to be removed after
      // Location mapping for QA environment
      const locationMapping = {
        'Balance Grille': "Tom's Grille and Bar",
        'Balance Campus': 'Commissary',
        'Balance Cleveland': 'Miami',
        'Balance Downtown Cleveland': 'Midtown',
        'Balance Downtown Toledo': 'Aventura',
        'Balance Franchise Group': 'Franchise Group',
        'Balance Freedom Street Arvada': 'Orlando',
        'Balance Perrysburg': 'Boynton Beach',
        'Balance Sylvania': 'Boca Raton',
        'Balance Syndicated': 'Head Office',
        'Balance Wylie': 'Naples',
        'Syndicate B': 'Ft. Lauderdale',
        'Balance Wiseman Enterprises': 'Pompano Beach',
      };

      // Check if the value exactly matches any of the keys in the mapping
      if (locationMapping.hasOwnProperty(value)) {
        return locationMapping[value];
      }

      // If no match is found, return the original value
      return value;
    }
    return value;
  };

  const handleDownload = async () => {
    if (!transactions.data || !transactions.data.length) return;

    setIsDownloading(true);

    try {
      // Fetch all transactions for download
      const allTransactionsData = await fetchAllTransactions();

      if (
        !allTransactionsData ||
        !allTransactionsData.data ||
        !allTransactionsData.data.length
      ) {
        console.error('No transaction data available for download');
        alert('No transaction data available for download');
        return;
      }

      // Prepare headers from columns
      const headers = allTransactionsData.columns.map(column => column.value);

      // Prepare data rows
      const rows = allTransactionsData.data.map(transaction => {
        return allTransactionsData.columns.map(column => {
          let value = transaction[column.key];
          // Process location values for QA environment
          value = processLocationValue(column.key, value);
          // Handle special formatting for numeric values
          if (column.key === 'Amount' || column.key === 'Tax Amount') {
            return value || '';
          }
          return value || '';
        });
      });

      // Create CSV content
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(',')),
      ].join('\n');

      // Create and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      // Use the appropriate date format for the filename
      const fromDateStr = isPeriodRange ? periodFrom : fromDate;
      const toDateStr = isPeriodRange ? periodTo : toDate;

      link.setAttribute(
        'download',
        `transactions_${accountKey}_${fromDateStr}_to_${toDateStr}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading transactions:', error);
      alert('Error downloading transactions. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoading) {
    return <FullScreenLoader message="Loading transactions..." />;
  }

  return (
    <Box bg={bgColor} borderRadius="lg" overflow="hidden">
      <Box p={4}>
        <Flex
          alignItems="center"
          gap={2}
          mb={4}
          cursor="pointer"
          onClick={() => setSelectedTransaction(null)}
          _hover={{ color: 'blue.500' }}
        >
          <Box bg={'#F4F7FB'} borderRadius="50%" p={1}>
            <ArrowLeftIcon width={18} height={18} />
          </Box>
          <Text fontSize={tokens.fontSize100} fontWeight={500}>
            Back to Cost
          </Text>
        </Flex>
        <Flex justifyContent="space-between" alignItems="center">
          <Text fontSize={20} fontWeight={600}>
            Transactions for {transactions?.data?.[0]?.Name || accountKey || ''}
          </Text>
          <IconButton
            icon={DownloadIcon}
            variant="tonal"
            label={isDownloading ? 'Downloading...' : 'Download'}
            onClick={handleDownload}
            disabled={isDownloading}
            isLoading={isDownloading}
          />
        </Flex>
      </Box>

      <Box overflowX="auto">
        {!transactions ||
        !transactions.columns ||
        transactions.columns.length === 0 ? (
          <Text textAlign="center" p={4}>
            No transaction data available
          </Text>
        ) : (
          <Table
            fontSize={tokens.fontSize100}
            minWidth="100%"
            width="max-content"
          >
            <TableHead>
              <TableRow variant="filled">
                {transactions.columns.map(column => (
                  <TableHeaderSortCell
                    key={column.key}
                    onSort={direction => handleSort(column.key, direction)}
                    direction={
                      sortConfig.key === column.key
                        ? sortConfig.direction
                        : undefined
                    }
                    textAlign={
                      column.key === 'Amount' ||
                      column.key === 'Tax Amount' ||
                      column.key === 'subt_nat_amount'
                        ? 'right'
                        : 'left'
                    }
                    minWidth={column.minWidth || 100}
                    maxWidth="200px"
                  >
                    {column.value}
                  </TableHeaderSortCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!Array.isArray(sortedTransactions) ||
              sortedTransactions.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={transactions.columns.length || 1}
                    textAlign="center"
                  >
                    No transactions found
                  </TableCell>
                </TableRow>
              ) : (
                sortedTransactions.map((transaction, index) => (
                  <TableRow key={index}>
                    {transactions.columns.map(column => (
                      <TableCell
                        key={column.key}
                        textAlign={
                          column.key === 'Amount' ||
                          column.key === 'Tax Amount' ||
                          column.key === 'subt_nat_amount'
                            ? 'right'
                            : 'left'
                        }
                        minWidth={column.minWidth || 100}
                        maxWidth="200px"
                      >
                        {processLocationValue(
                          column.key,
                          transaction[column.key] || '-'
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </Box>

      <Box p={4} borderTopWidth="1px" borderColor={borderColor}>
        <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
          <IconButton
            icon={ChevronLeftIcon}
            variant="tonal"
            label="Previous"
            size="md"
            isDisabled={currentPage === 1 || isLoading}
            onClick={() => handlePageChange(currentPage - 1)}
          />
          {renderPageNumbers()}
          <IconButton
            icon={ChevronRightIcon}
            variant="tonal"
            label="Next"
            size="md"
            isDisabled={currentPage === totalPages || isLoading}
            onClick={() => handlePageChange(currentPage + 1)}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default TransactionDetailPage;
